/**
 * CombatantsList — rail section showing PC and NPC combatant rows.
 *
 * PC rows: PersonaAvatar resolving the participant's primary-persona thumbnail
 *   (serializer `thumbnail_url` / `thumbnail_media_url`; resolved server-side with
 *   select_related, no per-PC query), falling back to a name-derived initial letter.
 *   Source: EncounterDetail.participants.
 *
 * NPC rows: PersonaAvatar resolving the opponent's persona thumbnail (serializer
 *   `thumbnail_url` / `thumbnail_media_url`), falling back to a name-derived initial
 *   letter when the opponent has no persona. Source: EncounterDetail.opponents.
 *
 * NPCs are visually distinct from PCs via a destructive-tinted border + background.
 *
 * Condition badges: each row renders its `active_conditions` as ConditionBadge
 *   chips that deep-link to the shared condition-detail modal on click.
 *
 * Phase 8, Task 8.3 — unified-combat-ui plan.
 */

import { cn } from '@/lib/utils';
import { PersonaAvatar } from '@/components/PersonaAvatar';
import { ConditionBadge } from '../components/ConditionBadge';
import type { EncounterDetail, Participant, Opponent } from '../types';
import type { components } from '@/generated/api';

type ConditionInstance = components['schemas']['ConditionInstance'];

// ---------------------------------------------------------------------------
// ConditionRow — flex row of condition badges for a combatant
// ---------------------------------------------------------------------------

interface ConditionRowProps {
  /**
   * active_conditions is typed loosely as `{[key: string]: unknown}[]` on the
   * generated Participant/Opponent schemas (it's a SerializerMethodField); each
   * entry is really a ConditionInstance. Cast at the boundary.
   */
  conditions?: { [key: string]: unknown }[];
}

function ConditionRow({ conditions }: ConditionRowProps) {
  if (!conditions || conditions.length === 0) {
    return null;
  }
  // Backend already orders by display_priority desc; be defensive and re-sort.
  const sorted = [...(conditions as ConditionInstance[])].sort(
    (a, b) => b.display_priority - a.display_priority
  );
  return (
    <div className="mt-1 flex flex-wrap gap-1" data-testid="condition-row">
      {sorted.map((condition) => (
        <ConditionBadge key={condition.id} condition={condition} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CombatantsListProps {
  encounter: EncounterDetail;
  /** Whether the section is collapsed. Controlled by parent (Task 8.6). */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

// ---------------------------------------------------------------------------
// HP mini-bar
// ---------------------------------------------------------------------------

interface HpBarProps {
  health: number | null;
  maxHealth: number | null;
  className?: string;
}

function HpBar({ health, maxHealth, className }: HpBarProps) {
  const pct =
    health !== null && maxHealth !== null && maxHealth > 0
      ? Math.min(100, (health / maxHealth) * 100)
      : 0;
  const isWounded = health !== null && maxHealth !== null && health / maxHealth < 0.5;

  return (
    <div className={cn('h-1.5 w-full overflow-hidden rounded-full bg-muted', className)}>
      <div
        className={cn(
          'h-full rounded-full transition-all',
          isWounded ? 'bg-amber-500' : 'bg-emerald-500'
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// ParticipantRow — one PC combatant row
// ---------------------------------------------------------------------------

interface ParticipantRowProps {
  participant: Participant;
}

function ParticipantRow({ participant }: ParticipantRowProps) {
  return (
    <div
      className="flex items-center gap-2 rounded p-1.5 hover:bg-accent/30"
      data-testid={`participant-row-${participant.id}`}
    >
      {/* Avatar — portrait resolves via the PC's primary persona thumbnail;
       * falls back to an initial-letter avatar when there is none (#630).
       */}
      <PersonaAvatar
        source={{
          name: participant.character_name,
          thumbnailUrl: participant.thumbnail_url,
          thumbnailMediaUrl: participant.thumbnail_media_url,
        }}
        size="sm"
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="truncate text-xs font-medium text-foreground">
            {participant.character_name}
          </p>
          {participant.current_position && (
            <span
              data-testid="position-badge"
              className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
            >
              {participant.current_position.name}
            </span>
          )}
        </div>
        {/* HP mini-bar */}
        <HpBar health={participant.health} maxHealth={participant.max_health} className="mt-0.5" />
        {/* Condition badges — deep-link to the condition-detail modal on click. */}
        <ConditionRow conditions={participant.active_conditions} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OpponentRow — one NPC combatant row
// ---------------------------------------------------------------------------

interface OpponentRowProps {
  opponent: Opponent;
}

function OpponentRow({ opponent }: OpponentRowProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded border border-destructive/30 bg-destructive/5 p-1.5',
        'hover:bg-destructive/10'
      )}
      data-testid={`opponent-row-${opponent.id}`}
    >
      {/* Avatar — portrait resolves via the opponent's persona thumbnail; falls
       * back to an initial-letter avatar when the opponent is persona-less.
       */}
      <PersonaAvatar
        source={{
          name: opponent.name,
          thumbnailUrl: opponent.thumbnail_url,
          thumbnailMediaUrl: opponent.thumbnail_media_url,
        }}
        size="sm"
      />

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="truncate text-xs font-medium text-foreground">{opponent.name}</p>
          {opponent.current_position && (
            <span
              data-testid="position-badge"
              className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] text-muted-foreground"
            >
              {opponent.current_position.name}
            </span>
          )}
        </div>
        {/* HP mini-bar */}
        <HpBar health={opponent.health} maxHealth={opponent.max_health} className="mt-0.5" />
        {/* Condition badges — deep-link to the condition-detail modal on click. */}
        <ConditionRow conditions={opponent.active_conditions} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CombatantsList
// ---------------------------------------------------------------------------

export function CombatantsList({
  encounter,
  collapsed = false,
  onToggleCollapse,
}: CombatantsListProps) {
  const { participants, opponents } = encounter;

  return (
    <div className="rounded-md border border-border bg-card" data-testid="combatants-list-section">
      {/* Section header */}
      <button
        type="button"
        onClick={onToggleCollapse}
        className="flex w-full items-center justify-between px-3 py-2 text-left"
        aria-expanded={!collapsed}
        data-testid="combatants-list-toggle"
      >
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Combatants
        </span>
        <span
          className={cn(
            'text-muted-foreground transition-transform',
            collapsed ? '-rotate-90' : 'rotate-0'
          )}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      {/* Content */}
      {!collapsed && (
        <div className="space-y-2 border-t border-border px-3 py-2">
          {/* PC section */}
          {participants.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Players
              </p>
              {participants.map((p) => (
                <ParticipantRow key={p.id} participant={p} />
              ))}
            </div>
          )}

          {/* NPC section */}
          {opponents.length > 0 && (
            <div className="space-y-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                Opponents
              </p>
              {opponents.map((o) => (
                <OpponentRow key={o.id} opponent={o} />
              ))}
            </div>
          )}

          {participants.length === 0 && opponents.length === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="combatants-empty">
              No combatants yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
